/* =====================================================================
   Ornaya — Core script
   - Initialises Lenis smooth scroll (if enabled in theme settings)
   - Global IntersectionObserver for [data-reveal] + [data-masked-line]
   - Header scroll-position listener (sets [data-scrolled] on <html>)
   - Public helpers: window.Ornaya.observe(el), Ornaya.unobserve(el)
   Loaded once from layout/theme.liquid, after ornaya-core.css.
   ===================================================================== */
(function () {
  'use strict';

  const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* ────────── Lenis smooth scroll ────────── */
  let lenis = null;
  function initLenis() {
    if (reduced) return;
    if (!window.Lenis) return;
    lenis = new window.Lenis({
      duration: 1.15,
      easing: (t) => Math.min(1, 1.001 - Math.pow(2, -10 * t)),
      smoothWheel: true,
      wheelMultiplier: 1,
      touchMultiplier: 1.4,
    });
    function raf(time) { lenis.raf(time); requestAnimationFrame(raf); }
    requestAnimationFrame(raf);
    window.Ornaya.lenis = lenis;
  }

  /* ────────── Reveal observer ────────── */
  const io = ('IntersectionObserver' in window)
    ? new IntersectionObserver(
        (entries) => {
          for (const entry of entries) {
            if (entry.isIntersecting) {
              entry.target.setAttribute('data-reveal-shown', '');
              if (entry.target.dataset.revealRepeat !== 'true') {
                io.unobserve(entry.target);
              }
            } else if (entry.target.dataset.revealRepeat === 'true') {
              entry.target.removeAttribute('data-reveal-shown');
            }
          }
        },
        { threshold: 0.15, rootMargin: '0px 0px -8% 0px' },
      )
    : null;

  function observe(el) {
    if (!el || !io) {
      if (el) el.setAttribute('data-reveal-shown', '');
      return;
    }
    io.observe(el);
  }

  function rebindReveals(root) {
    const scope = root || document;
    scope
      .querySelectorAll('[data-reveal]:not([data-reveal-observed]), [data-masked-line]:not([data-reveal-observed])')
      .forEach((el) => { el.setAttribute('data-reveal-observed', ''); observe(el); });
  }

  /* ────────── Header scroll listener ────────── */
  function bindHeaderScroll() {
    const root = document.documentElement;
    const onScroll = () => {
      if (window.scrollY > 30) root.setAttribute('data-scrolled', '');
      else root.removeAttribute('data-scrolled');
    };
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
  }

  /* ────────── Public API ────────── */
  window.Ornaya = window.Ornaya || {};
  window.Ornaya.observe = observe;
  window.Ornaya.unobserve = (el) => io && io.unobserve(el);
  window.Ornaya.rebindReveals = rebindReveals;
  window.Ornaya.reduced = reduced;

  /* ────────── Boot ────────── */
  function boot() {
    initLenis();
    bindHeaderScroll();
    rebindReveals(document);
    // Re-bind on Shopify section reload (theme editor)
    document.addEventListener('shopify:section:load', (e) => rebindReveals(e.target));
    document.addEventListener('shopify:section:unload', (e) => {
      e.target.querySelectorAll('[data-reveal-observed]').forEach((el) => {
        el.removeAttribute('data-reveal-observed');
        el.removeAttribute('data-reveal-shown');
      });
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
